import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CatchRow } from './catchRow';

interface CatchTableColumns {
  spot?: boolean;
  angler?: boolean;
  session?: boolean;
}

interface CatchTableProps {
  rows: CatchRow[];
  columns?: CatchTableColumns;
  renderActions?: (row: CatchRow) => ReactNode;
  onRowClick?: (row: CatchRow) => void;
}

const th = 'px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400';
const td = 'px-5 py-3';
const dash = <span className="text-slate-400 dark:text-slate-500">—</span>;

export function CatchTable({ rows, columns = {}, renderActions, onRowClick }: CatchTableProps) {
  const navigate = useNavigate();
  const { spot = false, angler = false, session = false } = columns;
  const handleClick = onRowClick ?? ((row: CatchRow) => navigate(`/catches/${row.id}`));

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
            {spot && <th className={th}>Spot</th>}
            <th className={th}>Fish</th>
            <th className={th}>Lure</th>
            {angler && <th className={th}>Angler</th>}
            <th className={th}>Length</th>
            <th className={th}>Date</th>
            {session && <th className={th}>Session</th>}
            {renderActions && <th className="px-5 py-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
          {rows.map(row => (
            <tr
              key={row.id}
              className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer"
              onClick={() => handleClick(row)}
            >
              {spot && (
                <td className={`${td} text-slate-700 dark:text-slate-300`}>{row.spotName ?? dash}</td>
              )}
              <td className={`${td} font-medium text-slate-900 dark:text-slate-100`}>{row.fishTypeName}</td>
              <td className={`${td} text-slate-700 dark:text-slate-300`}>{row.lureTypeName ?? dash}</td>
              {angler && (
                <td className={`${td} text-slate-700 dark:text-slate-300`}>{row.anglerName ?? dash}</td>
              )}
              <td className={`${td} text-slate-500 dark:text-slate-400`}>
                {row.lengthInches != null ? `${row.lengthInches}"` : dash}
              </td>
              <td className={`${td} text-slate-500 dark:text-slate-400`}>
                {new Date(row.caughtAt).toLocaleString()}
              </td>
              {session && (
                <td className={`${td} text-slate-500 dark:text-slate-400`}>{row.sessionId ?? dash}</td>
              )}
              {renderActions && <td className={`${td} text-right`}>{renderActions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
